import { useQuery } from '@tanstack/react-query';
import { formatCount, formatDateTime } from '@/format';
import { useState, type ReactNode } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  type DetailTab,
  type GridColumn,
} from '@/components/desktop';
import { ErrorNote, Field, Pager, Select, Table, errorMessage } from '@/components/ui';
import { useAuth } from '@/stores/auth';
import { toast } from '@/stores/toast';
import { describeDevice } from './settings/device';
import { AUDIT_ACTIONS, AUDIT_ACTION_LABEL, ENTITY_LABELS, entityLabel, humanise } from './auditWords';

type AuditRow = components['schemas']['AuditListItemDto'];

const PAGE_SIZE = 50;

const ACTIONS = AUDIT_ACTIONS;
const ACTION_LABEL = AUDIT_ACTION_LABEL;

interface Filters {
  actorId: string;
  action: string;
  entity: string;
  from: string;
  to: string;
  entityId: string;
  requestId: string;
}

const EMPTY: Filters = { actorId: '', action: '', entity: '', from: '', to: '', entityId: '', requestId: '' };

/** Date and time — for an audit trail, "when" needs the minute, not just the day. */
function when(value: string): string {
  return formatDateTime(value);
}

/**
 * The audit log: who did what, and when.
 *
 * Read-only by design — correcting a mistake happens in the screen that made
 * it (a reversing entry, a permission change), never by editing history here.
 * Laid out like every other list: filters on top, the grid, and the chosen
 * entry's changes in the details pane underneath.
 */
export function Audit() {
  const { can } = useAuth();
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [moreFilters, setMoreFilters] = useState(false);
  const [skip, setSkip] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [tab, setTab] = useState('changes');

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/api/v1/users'),
    enabled: can('iam.user.read'),
  });
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get('/api/v1/roles'),
    enabled: can('iam.role.read'),
  });
  const roleName = new Map((roles.data ?? []).map((r) => [r.code, r.nameEn]));
  /** "md, sup" → "Managing Director, Supervisor". */
  const rolesInWords = (codes: string | null) =>
    codes
      ? codes
          .split(',')
          .map((c) => roleName.get(c.trim()) ?? c.trim())
          .join(', ')
      : null;

  const filterQuery = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '')) as Record<string, string>;
  const query = { take: PAGE_SIZE, skip, ...filterQuery };

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['audit', query],
    queryFn: () => api.get('/api/v1/audit', query),
  });

  function update(patch: Partial<Filters>) {
    setFilters((f) => ({ ...f, ...patch }));
    setSkip(0);
    setSelectedKey(null);
  }

  const rows = data?.items ?? [];
  const selected = rows.find((r) => r.id === selectedKey) ?? null;
  const filtered = Object.keys(filterQuery).length > 0;

  const columns: Array<GridColumn<AuditRow>> = [
    { key: 'when', header: 'When', width: 150, render: (r) => <span className="dim">{when(r.createdAt)}</span> },
    {
      key: 'who',
      header: 'Who',
      width: 190,
      render: (r) => (
        <>
          {r.actorName ?? <span className="dim">System</span>}
          {r.actorRole && <span className="dim"> · {rolesInWords(r.actorRole)}</span>}
        </>
      ),
    },
    { key: 'action', header: 'What', width: 140, render: (r) => <span className="pill">{ACTION_LABEL.get(r.action) ?? r.action}</span> },
    {
      key: 'record',
      header: 'Record',
      width: 170,
      render: (r) => (
        <>
          {entityLabel(r.entity)}
          {r.entityId && <span className="dim"> #{r.entityId}</span>}
        </>
      ),
    },
    { key: 'detail', header: 'Detail', width: 320, render: (r) => <span className="dim">{r.detail ?? '—'}</span> },
    { key: 'diff', header: 'Changes', width: 90, render: (r) => (r.hasDiff ? 'Yes' : <span className="dim">—</span>) },
  ];

  const tabs: DetailTab[] = selected
    ? [
        { id: 'changes', label: 'What changed', icon: 'edit', content: <AuditChanges id={selected.id} /> },
        {
          id: 'details',
          label: 'Details',
          icon: 'details',
          content: (
            <AuditFacts
              row={selected}
              roles={rolesInWords(selected.actorRole)}
              onPerson={
                selected.actorId
                  ? () => {
                      setFilters({ ...EMPTY, actorId: selected.actorId! });
                      setSkip(0);
                    }
                  : undefined
              }
              onRecord={
                selected.entityId
                  ? () => {
                      setFilters({ ...EMPTY, entity: selected.entity, entityId: selected.entityId! });
                      setMoreFilters(true);
                      setSkip(0);
                    }
                  : undefined
              }
            />
          ),
        },
      ]
    : [];

  const first = data && data.total > 0 ? skip + 1 : 0;
  const last = data ? Math.min(skip + PAGE_SIZE, data.total) : 0;

  return (
    <div className="workbench">
      <SplitView
        id="iam-audit"
        direction="column"
        initial={400}
        firstLabel="audit list"
        first={
          <div className="workbench__main">
            <div className="filterbar">
              {can('iam.user.read') && can('iam.history.read') && (
                <Select
                  label="Person"
                  value={filters.actorId}
                  onChange={(actorId) => update({ actorId })}
                  placeholder="Anyone"
                  options={(users.data ?? []).map((u) => ({ value: u.id, label: `${u.fullName} (${u.userId})` }))}
                />
              )}
              <Select
                label="What"
                value={filters.action}
                onChange={(action) => update({ action })}
                placeholder="Anything"
                options={ACTIONS}
              />
              <Select
                label="Record type"
                value={filters.entity}
                onChange={(entity) => update({ entity })}
                placeholder="Any record"
                options={Object.entries(ENTITY_LABELS)
                  .map(([value, label]) => ({ value, label }))
                  .sort((a, b) => a.label.localeCompare(b.label))}
              />
              <Field label="From" type="date" value={filters.from} onChange={(from) => update({ from })} />
              <Field label="To" type="date" value={filters.to} onChange={(to) => update({ to })} />
            </div>
            {moreFilters && (
              <div className="filterbar">
                <Field label="Record number" value={filters.entityId} onChange={(entityId) => update({ entityId })} />
                <Field label="Request number" value={filters.requestId} onChange={(requestId) => update({ requestId })} />
              </div>
            )}

            <ActionBar
              actions={[
                { label: moreFilters ? 'Fewer filters' : 'More filters', onSelect: () => setMoreFilters((v) => !v) },
                {
                  label: 'Clear filters',
                  icon: 'clear',
                  onSelect: () => {
                    setFilters(EMPTY);
                    setSkip(0);
                    setSelectedKey(null);
                  },
                  enabled: filtered,
                  reason: 'No filters are set',
                },
                {
                  // The download is itself recorded in the audit log.
                  label: 'Download',
                  icon: 'export',
                  allowed: can('iam.audit.export'),
                  onSelect: () =>
                    void api
                      .download('/api/v1/audit/export', 'audit-log.csv', filterQuery)
                      .catch((e: unknown) => toast.error(errorMessage(e, 'Could not download the log'))),
                },
              ]}
            />

            {isError && <ErrorNote message={error.message} />}

            {!can('iam.history.read') && (
              <p className="muted">
                Your own entries. Seeing everyone's is a separate permission, so the Person filter is
                not offered here.
              </p>
            )}

            <DataGrid
              id="iam-audit"
              label="Audit entries"
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              loading={isPending}
              empty={filtered ? 'Nothing matches these filters.' : 'Nothing has been recorded yet.'}
            />

            <Pager skip={skip} take={PAGE_SIZE} total={data?.total ?? 0} onChange={(s) => { setSkip(s); setSelectedKey(null); }} />
          </div>
        }
        second={
          <DetailPane
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            empty="Choose an entry to see exactly what changed."
            title={
              selected && (
                <span>
                  {ACTION_LABEL.get(selected.action) ?? selected.action} · {entityLabel(selected.entity)}
                  {selected.entityId && ` #${selected.entityId}`}
                </span>
              )
            }
          />
        }
      />
      <StatusBar>
        <span>
          Showing {first}–{last} of {formatCount(data?.total ?? 0)}
        </span>
        {filtered && <span>Filtered</span>}
        <span>{selected ? `Chosen: entry #${selected.id}` : 'No entry chosen'}</span>
      </StatusBar>
    </div>
  );
}

function DetailItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

function AuditFacts({
  row,
  roles,
  onPerson,
  onRecord,
}: {
  row: AuditRow;
  roles: string | null;
  onPerson?: (() => void) | undefined;
  onRecord?: (() => void) | undefined;
}) {
  const detail = useQuery({
    queryKey: ['audit', 'entry', row.id],
    queryFn: () => api.get(`/api/v1/audit/${row.id}` as '/api/v1/audit/{id}'),
  });
  return (
    <div className="stack">
      <dl className="detaillist">
        <DetailItem label="When" value={when(row.createdAt)} />
        <DetailItem label="Who" value={row.actorName ? `${row.actorName}${roles ? ` (${roles})` : ''}` : 'System'} />
        <DetailItem label="What" value={ACTION_LABEL.get(row.action) ?? row.action} />
        <DetailItem label="Record" value={`${entityLabel(row.entity)}${row.entityId ? ` #${row.entityId}` : ''}`} />
        <DetailItem label="Detail" value={row.detail ?? '—'} />
        <DetailItem label="Device" value={detail.data ? describeDevice(detail.data.userAgent) : '…'} />
        <DetailItem label="Network address" value={row.ipAddress ?? '—'} />
        <DetailItem label="Browser tab" value={row.sessionId ?? '—'} />
        <DetailItem label="Request number" value={row.requestId ?? '—'} />
      </dl>
      <div className="settingsapp__blockrow">
        {onPerson && (
          <button type="button" className="btn btn--sm" onClick={onPerson}>
            Everything this person did
          </button>
        )}
        {onRecord && (
          <button type="button" className="btn btn--sm" onClick={onRecord}>
            Everything about this record
          </button>
        )}
      </div>
    </div>
  );
}

/** The field-by-field before/after, computed by the server. */
function AuditChanges({ id }: { id: string }) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['audit', 'entry', id],
    queryFn: () => api.get(`/api/v1/audit/${id}` as '/api/v1/audit/{id}'),
  });

  if (isPending) return <p className="muted">Loading…</p>;
  if (isError) return <ErrorNote message={error.message} />;

  return (
    <div className="stack">
      {data.masked && (
        <p className="muted">
          Some fields are hidden because you do not have the permission to see them. What is shown is correct, but may not be
          everything.
        </p>
      )}
      {data.changes.length === 0 ? (
        <p className="muted">No field-by-field changes were recorded for this entry.</p>
      ) : (
        <Table
          count={data.changes.length}
          empty=""
          head={
            <tr>
              <th>Field</th>
              <th>Before</th>
              <th>After</th>
            </tr>
          }
        >
          {data.changes.map((c, i) => (
            <tr key={i}>
              <td title={c.field}>{humanise(c.field)}</td>
              <td className="dim">{formatValue(c.before)}</td>
              <td>{formatValue(c.after)}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}
